import { createSlice } from '@reduxjs/toolkit';
import { Master, EErrorCode, EFeatureStatus, EWorkItemType } from '../../../data/Constants';
import { flipCountries } from '../../../services/DataHelperFunctions';
import { getPMPPropertyStatus } from '../../../services/PropertyHelperFunctions';
import {
  mapMAToFeatureState,
  getFeatureStatus,
  createPaginatedFeatures,
} from '../../Helper/SliceDataHelper';
import type { IFeature, IWorkItemOption } from '../../../../types';

import { initialState } from './ProcosInitialState';
import { marketAssociationReducers } from './ProcosMarketAssociationReducers';
import { workItemReducers } from './ProcosWorkItemReducers';
import { featureReducers } from './ProcosFeatureReducers';
import { productReducers } from './ProcosProductReducers';
import {
  updateSaveStatusOfBatches,
  updateSaveStatusOfEffectivity,
  updateSaveStatusOfFeatureRules,
} from './ProcosReducerHelpers';
import {
  createEffectivity,
  createRules,
  createWorkItem,
  getFeatures,
  getMarketAssociations,
  getMarkets,
  getWorkItems,
} from './ProcosThunks';

//initialize the action to set the state variables
const ProcosSlice = createSlice({
  name: 'procos',
  initialState,
  reducers: {
    ...marketAssociationReducers,
    ...workItemReducers,
    ...featureReducers,
    ...productReducers,
    resetIsDataUpdated(state) {
      state.isDataUpdated = initialState.isDataUpdated;
    },
    setResetDataFlag(state, action) {
      state.isDataReset = action.payload;
    },
    setBatchSaveStatus(state, action) {
      state.batchSaveStatus.push(action.payload);
    },
    resetBatchSaveStatus(state) {
      state.batchSaveStatus = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkItems.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          return;
        }
        let workItemList: IWorkItemOption[] = [Master];
        workItemList = [...workItemList, ...action.payload.data];
        state.workItems = workItemList;
      })
      .addCase(getMarkets.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          state.markets = initialState.markets;
        } else {
          state.markets = action.payload.data;
        }
      })
      .addCase(getFeatures.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          return;
        }
        state.productStatus = getPMPPropertyStatus(action.payload.data)
          ? EFeatureStatus.Active
          : EFeatureStatus.InActive;
        action.payload.data = action.payload.data.filter(
          (feature: IFeature) => feature.code !== 'PMP'
        );
        const featuresData = createPaginatedFeatures(action.payload, state.features.data);
        state.features.data = featuresData;
        state.features.status.initial = getFeatureStatus(action.payload);
        state.features.status.updated = state.features.status.initial;
        //Set the below state with product status in master
        //This is used in the places to perform functionalities based on Product's status in the Master (E.g.: Send To Sofon)
        if (state.activeWorkItem?.name === EWorkItemType.Master) {
          state.productStatusInMaster = state.productStatus;
        }
      })
      .addCase(getMarketAssociations.fulfilled, (state, action) => {
        state.isDataUpdated = false;
        state.isProductStatusUpdated = false;
        const allMarkets = state.markets.map((market) => market.code);
        if ('error' in action.payload) {
          state.features.ma = {};
          state.product.ma = initialState.product.ma;
        } else {
          const product = action.payload.data.find((x) => x.code === state.product.id);
          if (product) {
            product.countries =
              product.includeMarkets === false
                ? flipCountries(allMarkets, product.countries)
                : product.countries;
            state.product.ma = {
              initialMarkets: product.countries,
              updatedMarkets: product.countries,
            };
          }
          //state.product.rows = addRfq(product.countries, state.product.rows)
          state.features.ma = mapMAToFeatureState(
            action.payload.data,
            state.product.id,
            product.countries,
            allMarkets
          );
        }
      })
      .addCase(createWorkItem.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          return;
        }
        const name = action.payload.data?.name;
        const id = action.payload.data?.id;
        if (!name || !id) {
          return;
        }
        state.activeWorkItem = { name, id };
      })
      .addCase(createRules.pending, (state) => {
        state.isCreateRuleInvoked = true;
        state.isBatchSaveComplete = false;
      })
      .addCase(createRules.fulfilled, (state, action) => {
        // Get all feature codes sent in payload to the API
        const marketRuleFeatureCodes = action?.meta?.arg?.request.marketRules?.map(
          (item) => item.code
        );

        state.isBatchSaveComplete =
          action?.meta?.arg?.isAllBatchesProcessed || !!action.payload.data?.errorId;

        // Get all feature codes which are failed to update
        if (action.payload.data) {
          //Dont reset the isDataUpdated flag  if API failed by network error to enable the Save and Revert button
          if (action.payload?.data?.errorId !== EErrorCode.NetworkError) {
            //Reset the isDataUpdated updated after all the batches are completed
            if (action?.meta?.arg?.isAllBatchesProcessed) {
              state.isDataUpdated = false;
            }

            //Reset the updated markets after api is completed
            //This should not update when api failed  by network issue
            marketRuleFeatureCodes.forEach((key) => {
              //update new rules to  initial markets for product
              if (state.product.id === key) {
                state.product.ma.initialMarkets = state.product.ma.updatedMarkets;
              }
              //update new rules to  initial markets for feature and options
              if (state.features.ma.hasOwnProperty(key)) {
                state.features.ma[key].initialMarkets = state.features.ma[key].updatedMarkets;
              }
            });
          }
          state.isProductStatusUpdated = false;
        }

        updateSaveStatusOfFeatureRules(state, action.payload.data, marketRuleFeatureCodes);
        updateSaveStatusOfBatches(state, action.payload.data);
      })
      .addCase(createEffectivity.fulfilled, (state, action) => {
        // Get all feature codes sent in payload to the API
        const effectivityFeatures = action?.meta?.arg?.features?.map((item) => item.code);
        // Get Response from Effectivity Save API
        const responseData = action.payload.data;

        if (responseData) {
          // To disable the Save and Revert buttons in ProCos make isDataUpdated false
          state.isDataUpdated = false;
          state.effectivityResult = responseData;
          const effectivityResult = responseData;
          if (Object.keys(effectivityResult).length !== 0) {
            if (state.product.id in effectivityResult) {
              state.productStatus =
                state.productStatus === EFeatureStatus.Active
                  ? EFeatureStatus.InActive
                  : EFeatureStatus.Active;
            }
            Object.keys(effectivityResult).forEach((key) => {
              if (state.features.status.initial && state.features.status.updated) {
                if (
                  key in effectivityResult &&
                  Object.keys(state.features.status.initial).includes(key)
                ) {
                  state.features.status.updated[key] = state.features.status.initial[key];
                }
              }
            });
          }
          // active status of features was not updated properly
          state.features.status.initial = state.features.status.updated;

          //Setting this state once effectivity API is executed
          updateSaveStatusOfEffectivity(state, effectivityFeatures, responseData);
        }
      });
  },
});

export const {
  updateToInitialFeatureMA,
  resetProductData,
  revertMarketAssociations,
  setProduct,
  updateProductMarketAssociations,
  updateFeatureMarketAssociations,
  setActiveWorkItem,
  resetIsDataUpdated,
  setResetDataFlag,
  setNewWorkItem,
  resetFeatureMA,
  setEditMode,
  setProductStatus,
  setFeatureStatus,
  setFeatureData,
  updateFeatureStatus,
  setBatchSaveStatus,
  resetBatchSaveStatus,
  resetSaveStatusOfFeatures,
  setFeatureRecordsPerPage,
  setStatusOfAllRuleUpdates,
} = ProcosSlice.actions;

export default ProcosSlice.reducer;
